<template>
  <div class="expense-period-bulk-closure pa-10">

    <title-page icon="mdi-cash-check">
      {{ $t('expense-refund.bulk-closure.title') }}
    </title-page>

    <!-- Filters -->
    <v-row class="mb-4" align="center">
      <v-col cols="12" md="3">
        <date-picker
            v-model="selectedMonth"
            type="month"
            :label="$t('expense-refund.bulk-closure.period')"
            outlined
            dense
            hide-details
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-btn-toggle v-model="refundHandling" mandatory dense>
          <v-btn :value="null" small>{{ $t('all') }}</v-btn>
          <v-btn :value="true" small>{{ $t('expense-refund.bulk-closure.managed-by-us') }}</v-btn>
          <v-btn :value="false" small>{{ $t('expense-refund.bulk-closure.not-managed') }}</v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col cols="12" md="2">
        <v-select
            v-model="statusFilter"
            :items="statusFilterOptions"
            :label="$t('status')"
            dense outlined hide-details clearable
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-checkbox
            v-model="showEmpty"
            :label="$t('expense-refund.bulk-closure.show-empty')"
            hide-details
            dense
            class="mt-0"
        />
      </v-col>
      <v-col cols="12" md="3" class="text-right">
        <v-btn
            small
            color="success"
            :disabled="selectedIds.length === 0 || closing || !monthEnded"
            :loading="closing"
            @click="confirmBulkClose"
        >
          <v-icon left small>mdi-lock</v-icon>
          {{ $t('expense-refund.bulk-closure.close-selected', { count: selectedIds.length }) }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- A month can only be closed once it is over: closing an unfinished period would invoice its proformas
         mid-cycle. Selection + closing are disabled until the month has ended (special cases go through the
         dedicated account-closure flow, never here). -->
    <v-alert v-if="!monthEnded" type="warning" text dense border="left" class="mb-4">
      {{ $t('expense-refund.bulk-closure.month-not-ended') }}
    </v-alert>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

    <v-data-table
        v-model="selected"
        :headers="headers"
        :items="filteredItems"
        :loading="loading"
        item-key="id"
        show-select
        :selectable-key="'_selectable'"
        hide-default-footer
        disable-pagination
        dense
        :item-class="itemRowClass"
    >
      <template #item.organization="{item}">
        <router-link
            v-if="item.organization"
            :to="{name: 'organizations.expense-periods', params: {id: item.organization.id || item.organization._id}}"
            target="_blank"
        >
          {{ item.organization.name }}
        </router-link>
      </template>

      <template #item.totalAmount="{item}">
        {{ item.totalAmount != null ? item.totalAmount.toFixed(2) + ' €' : '-' }}
      </template>

      <template #item.totalEnergy="{item}">
        {{ formatEnergy(item.totalEnergy) }}
      </template>

      <template #item.memberCount="{item}">
        {{ item.memberCount != null ? item.memberCount : '-' }}
      </template>

      <template #item.deviceCount="{item}">
        {{ item.deviceCount != null ? item.deviceCount : '-' }}
      </template>

      <template #item.deliveredEnergy="{item}">
        {{ formatEnergy(item.deliveredEnergy) }}
      </template>

      <template #item.anomalies="{item}">
        <v-btn icon x-small @click="checkAnomalies(item)" v-if="anomalyCache[item.id] != null">
          <v-icon small :color="anomalyCache[item.id] === 0 ? 'success' : 'orange'">
            {{ anomalyCache[item.id] === 0 ? 'mdi-check-circle' : 'mdi-alert' }}
          </v-icon>
        </v-btn>
        <v-progress-circular v-else indeterminate size="16" width="2" color="grey" />
      </template>

      <template #item.status="{item}">
        <v-chip x-small :color="statusColor(item.closureStatus || item.status)" dark>
          {{ $t('expense-refund.bulk-closure.statuses.' + (item.closureStatus || item.status)) }}
        </v-chip>
      </template>

      <template #item.actions="{item}">
        <v-btn
            v-if="item.closureStatus === 'IN_PROGRESS' || item.closureStatus === 'DONE' || item.closureStatus === 'FAILED'"
            icon x-small
            @click="showLog(item)"
        >
          <v-icon small>mdi-text-box-outline</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Anomalies dialog -->
    <v-dialog v-model="anomalyDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title><h3>{{ $t('anomaly.subscription-anomalies') }}</h3></v-card-title>
        <v-divider />
        <v-card-text style="max-height: 60vh; overflow-y: auto;" class="pt-4">
          <div v-if="anomalyLoading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>
          <subscription-anomaly-list
              v-if="anomalyDialog"
              ref="anomalyList"
              :organization-id="anomalyOrgId"
              :expense-period-id="anomalyPeriodId"
              @loading="anomalyLoading = $event"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="anomalyDialog = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Log dialog -->
    <v-dialog v-model="logDialog" max-width="700" scrollable>
      <v-card v-if="logItem">
        <v-card-title>
          <h3>{{ logItem.organization ? logItem.organization.name : '' }}</h3>
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 60vh; overflow-y: auto;" class="pt-4">
          <div v-if="logLoading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>
          <template v-if="logProgress">
            <v-progress-linear
                :value="logProgress.total > 0 ? (logProgress.done / logProgress.total) * 100 : 0"
                color="primary"
                height="20"
                striped
                class="mb-4"
            >
              <template #default>
                <strong class="white--text">{{ logProgress.done }} / {{ logProgress.total }}</strong>
              </template>
            </v-progress-linear>
            <v-list dense>
              <v-list-item v-for="(entry, idx) in logProgress.items" :key="idx" class="px-0">
                <v-list-item-icon class="mr-2">
                  <v-icon small :color="logItemColor(entry.status)">{{ logItemIcon(entry.status) }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="body-2">{{ entry.label || entry.memberId || entry.type }}</v-list-item-title>
                  <v-list-item-subtitle v-if="entry.errorMessage" class="error--text">{{ entry.errorMessage }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="logDialog = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation dialog -->
    <v-dialog v-model="confirmDialog" max-width="450">
      <v-card>
        <v-card-title>{{ $t('expense-refund.bulk-closure.confirm-title') }}</v-card-title>
        <v-card-text>
          {{ $t('expense-refund.bulk-closure.confirm-message', { count: selectedIds.length }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="confirmDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="success" @click="executeBulkClose">{{ $t('confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import ExpenseRefundRepository from "@repository/ExpenseRefundRepository";
import AnomalyRepository from "@repository/AnomalyRepository";
import SubscriptionAnomalyList from "@elements/SubscriptionAnomalyList.vue";
import DatePicker from "@blocks/DatePicker";
import dayjs from "dayjs";

export default {
  components: { DatePicker, SubscriptionAnomalyList },

  data() {
    return {
      loading: false,
      closing: false,
      selectedMonth: dayjs().subtract(1, 'month').format('YYYY-MM'),
      refundHandling: null,
      statusFilter: 'PENDING',
      showEmpty: false,
      items: [],
      selected: [],
      // Anomalies
      anomalyCache: {},
      anomalyDialog: false,
      anomalyOrgId: null,
      anomalyPeriodId: null,
      anomalyLoading: false,
      // Log
      logDialog: false,
      logItem: null,
      logProgress: null,
      logLoading: false,
      logPollTimer: null,
      // Confirm
      confirmDialog: false,
      // Bulk polling
      bulkPollTimer: null,
      closingIds: [],
    }
  },

  mounted() {
    this.loadData()
  },

  beforeDestroy() {
    this.clearTimers()
  },

  computed: {
    headers() {
      return [
        { text: this.$t('organization.organization'), value: 'organization', sortable: false, align: 'left' },
        { text: this.$t('expense-refund.bulk-closure.members'), value: 'memberCount', width: 90 },
        { text: this.$t('expense-refund.bulk-closure.devices'), value: 'deviceCount', width: 100 },
        { text: this.$t('expense-refund.bulk-closure.refunded-energy'), value: 'totalEnergy', width: 130 },
        { text: this.$t('expense-refund.bulk-closure.delivered-energy'), value: 'deliveredEnergy', width: 130 },
        { text: this.$t('amount'), value: 'totalAmount', width: 120 },
        { text: this.$t('expense-refund.bulk-closure.anomalies-col'), value: 'anomalies', width: 90, sortable: false },
        { text: this.$t('status'), value: 'status', width: 120 },
        { text: '', value: 'actions', sortable: false, width: 50 },
      ]
    },

    statusFilterOptions() {
      return [
        { text: this.$t('expense-refund.bulk-closure.statuses.PENDING'), value: 'PENDING' },
        { text: this.$t('expense-refund.bulk-closure.statuses.DONE'), value: 'DONE' },
      ]
    },

    filteredItems() {
      let result = this.items
      if (!this.showEmpty) {
        result = result.filter(i => i.memberCount > 0 || i.totalAmount > 0)
      }
      if (this.statusFilter) {
        result = result.filter(i => (i.closureStatus || i.status) === this.statusFilter)
      }
      // Add _selectable flag: only PENDING items, and only once the month is over, can be selected.
      return result.map(i => ({ ...i, _selectable: (i.closureStatus || i.status) === 'PENDING' && this.monthEnded }))
    },

    selectedIds() {
      return this.selected.map(s => s.id)
    },

    // A month is closable only once it has fully ended (its last day is in the past). Guards against
    // closing/invoicing a period that is not over yet — the mirror of the backend guard.
    monthEnded() {
      return dayjs(this.selectedMonth + '-01').endOf('month').isBefore(dayjs())
    },
  },

  watch: {
    selectedMonth() {
      this.loadData()
    },
    refundHandling() {
      this.loadData()
    },
    showEmpty() {
      this.selected = []
    },
    statusFilter() {
      this.selected = []
    },
  },

  methods: {
    clearTimers() {
      if (this.logPollTimer) clearInterval(this.logPollTimer)
      if (this.bulkPollTimer) clearInterval(this.bulkPollTimer)
    },

    loadData() {
      this.loading = true
      this.selected = []
      this.anomalyCache = {}
      const params = { month: this.selectedMonth }
      if (this.refundHandling != null) params.refundHandling = this.refundHandling
      ExpenseRefundRepository.searchPeriods(params).then(response => {
        this.items = response.items || response
        // Pre-fill anomaly cache from backend Redis cache
        this.items.forEach(item => {
          if (item.anomaly != null) {
            this.$set(this.anomalyCache, item.id, this.countAnomalies(item.anomaly))
          }
        })
        this.loading = false
        // Only fetch anomalies for items without cache
        this.loadAnomaliesBatch(0)
      }).catch(err => {
        this.notifyError(err)
        this.loading = false
      })
    },

    loadAnomaliesBatch(startIndex) {
      const batchSize = 3
      const batch = this.items.slice(startIndex, startIndex + batchSize)
          .filter(item => this.anomalyCache[item.id] == null)
      if (batch.length === 0) {
        // Check if there are more items to process
        const nextStart = startIndex + batchSize
        if (nextStart < this.items.length) {
          this.loadAnomaliesBatch(nextStart)
        }
        return
      }

      const promises = batch.map(item => {
        const orgId = item.organization?.id || item.organization?._id
        return AnomalyRepository.subscription(orgId, item.id).then(data => {
          this.$set(this.anomalyCache, item.id, this.countAnomalies(data))
        }).catch(() => {
          this.$set(this.anomalyCache, item.id, -1)
        })
      })

      Promise.all(promises).then(() => {
        if (startIndex + batchSize < this.items.length) {
          this.loadAnomaliesBatch(startIndex + batchSize)
        }
      })
    },

    countAnomalies(anomaly) {
      let total = 0
      for (const key in anomaly) {
        if (key !== '_timings' && Array.isArray(anomaly[key])) total += anomaly[key].length
      }
      return total
    },

    formatEnergy(val) {
      if (val == null) return '-'
      if (val >= 1000000) return (val / 1000000).toFixed(2) + ' MWh'
      return (val / 1000).toFixed(1) + ' kWh'
    },

    statusColor(status) {
      const colors = {
        PENDING: 'grey',
        IN_PROGRESS: 'primary',
        DONE: 'success',
        FAILED: 'error',
        CANCELED: 'grey lighten-1',
      }
      return colors[status] || 'grey'
    },

    itemRowClass(item) {
      if (item.closureStatus === 'DONE') return 'green lighten-5'
      if (item.closureStatus === 'FAILED') return 'red lighten-5'
      if (item.closureStatus === 'IN_PROGRESS') return 'blue lighten-5'
      return ''
    },

    checkAnomalies(item) {
      this.anomalyOrgId = item.organization.id || item.organization._id
      this.anomalyPeriodId = item.id
      this.anomalyLoading = true
      this.anomalyDialog = true
    },

    confirmBulkClose() {
      this.confirmDialog = true
    },

    executeBulkClose() {
      this.confirmDialog = false
      if (!this.monthEnded) return // safety net: never close a month that is not over (backend enforces it too)
      this.closing = true
      this.closingIds = [...this.selectedIds]

      const promises = this.closingIds.map(id =>
          ExpenseRefundRepository.closePeriod(id).catch(err => ({ error: err, periodId: id }))
      )

      Promise.all(promises).then(results => {
        let errorCount = 0
        results.forEach(r => {
          if (r && r.error) {
            errorCount++
            this.notifyError(r.error)
          }
        })

        // Mark items as IN_PROGRESS
        this.closingIds.forEach(id => {
          const item = this.items.find(i => i.id === id)
          if (item) this.$set(item, 'closureStatus', 'IN_PROGRESS')
        })

        this.selected = []
        this.closing = false

        // Start polling for progress
        this.startBulkPolling()
      })
    },

    startBulkPolling() {
      if (this.bulkPollTimer) clearInterval(this.bulkPollTimer)
      this.bulkPollTimer = setInterval(() => {
        const inProgress = this.items.filter(i => i.closureStatus === 'IN_PROGRESS')
        if (inProgress.length === 0) {
          clearInterval(this.bulkPollTimer)
          this.bulkPollTimer = null
          return
        }
        inProgress.forEach(item => {
          ExpenseRefundRepository.closureProgress(item.id).then(progress => {
            if (progress.status === 'DONE' || progress.status === 'FAILED') {
              this.$set(item, 'closureStatus', progress.status)
            }
          }).catch(() => {})
        })
      }, 5000)
    },

    showLog(item) {
      this.logItem = item
      this.logProgress = null
      this.logLoading = true
      this.logDialog = true
      this.loadLog(item.id)

      if (this.logPollTimer) clearInterval(this.logPollTimer)
      if (item.closureStatus === 'IN_PROGRESS') {
        this.logPollTimer = setInterval(() => {
          this.loadLog(item.id)
        }, 3000)
      }
    },

    loadLog(periodId) {
      ExpenseRefundRepository.closureProgress(periodId).then(progress => {
        this.logProgress = progress
        this.logLoading = false
        if (progress.status === 'DONE' || progress.status === 'FAILED') {
          if (this.logPollTimer) {
            clearInterval(this.logPollTimer)
            this.logPollTimer = null
          }
        }
      }).catch(() => {
        this.logLoading = false
      })
    },

    logItemColor(status) {
      const colors = { DONE: 'success', FAILED: 'error', IN_PROGRESS: 'primary', PENDING: 'grey' }
      return colors[status] || 'grey'
    },

    logItemIcon(status) {
      const icons = { DONE: 'mdi-check-circle', FAILED: 'mdi-alert-circle', IN_PROGRESS: 'mdi-progress-clock', PENDING: 'mdi-clock-outline' }
      return icons[status] || 'mdi-clock-outline'
    },
  },
}
</script>
